import { useMemo } from 'react';
import type { Action } from '@shared/helpers/pipeline/update-engine.models';
import { ProposalId } from '@shared/models/proposals.model';
import { useSelectedVersion } from '@client/store/ui';
import { EMPTY_FROZEN_ARRAY } from '@shared/helpers/array.helpers';
import { useProposal } from './use-proposals.hooks';

/**
 * Returns the ordered list of {@link Action}s referenced by the given proposal.
 * Preserves the ordering from the proposal's `actionIds` field (see {@link IProposal}).
 */
export const useActionsForProposal = (proposalId?: ProposalId): Action[] => {
  const version = useSelectedVersion();
  const proposal = useProposal(proposalId);

  return useMemo(() => {
    if (!proposal || !version?.actions?.length) {
      return EMPTY_FROZEN_ARRAY as Action[];
    }

    const actionsById = new Map(version.actions.map((a) => [a.id, a]));
    return proposal.actionIds
      .map((id) => actionsById.get(id))
      .filter((a): a is Action => a !== undefined);
  }, [proposal, version?.actions]);
};
